import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    Divider,
    IconButton,
    InputAdornment,
    Link,
    Paper,
    Stack,
    TextField,
    Typography,
    useTheme,
    useMediaQuery
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { fetchWithError } from "../utils/fetchWithError.ts";
import { useSnackbar } from 'notistack';
import tintoaLogoWhite from "../img/logo_tintoa_white.png";
import tintoaLogoBlack from "../img/logo_tintoa_black.png";
import { TermsAndConditions } from "../components/TermsAndConditions.tsx";
import { AuthPageShell } from "../components/auth/AuthPageShell.tsx";
import { prepareLoginSession } from "./prepareLoginSession.ts";

// German messages keyed by IdsErrorCode. Used when the backend redirects back to
// /app/login?error=<code> after a failed login (e.g. wrong password, unknown
// account, locked account, missing client roles).
const LOGIN_ERROR_MESSAGES: Record<string, string> = {
    "1001": "Diese Client-Anwendung ist uns nicht bekannt.",
    "1002": "Dein Zugang ist noch nicht verifiziert. Bitte überprüfe deine E-Mails.",
    "1004": "E-Mail oder Passwort falsch. Bitte noch einmal versuchen.",
    "1005": "Dieser Zugang ist gesperrt. Bitte wende dich an unseren Support.",
    "1007": "Dieser Zugang existiert noch nicht. Registriere dich zunächst.",
    "1014": "Dieses Konto wurde mit Google erstellt. Bitte melde dich mit Google an.",
    "1016": "Für diese Anwendung fehlen dir die erforderlichen Rechte. Bitte wende dich an den Administrator der Anwendung.",
    "1018": "Die Google-E-Mail stimmt nicht mit deinem Konto überein. Verknüpfe das Google-Konto, das dieselbe E-Mail-Adresse verwendet.",
    "1019": "Bitte melde dich an, um dein Konto mit Google zu verknüpfen."
};

const GENERIC_LOGIN_ERROR =
    "Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut!";

export const LoginScreen = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [loginReady, setLoginReady] = useState(false);
    const [clientValidationError, setClientValidationError] = useState<string | null>(null);
    const [oauthClientName, setOauthClientName] = useState<string | null>(null);
    const [oauthClientIconUrl, setOauthClientIconUrl] = useState<string | null>(null);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const theme = useTheme();
    const isDarkTheme = theme.palette.mode === 'dark';
    const { enqueueSnackbar } = useSnackbar();
    const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));

    // Show error from URL after a failed login or Google OAuth redirect
    // (e.g. wrong password, unknown account, locked account, missing roles).
    useEffect(() => {
        const errorCode = searchParams.get("error");
        if (errorCode) {
            const message = LOGIN_ERROR_MESSAGES[errorCode] ?? GENERIC_LOGIN_ERROR;
            enqueueSnackbar(message, { variant: "error" });
            searchParams.delete("error");
            setSearchParams(searchParams, { replace: true });
        }
    }, []);

    // Probe session first so a stale httpOnly `tid` is cleared before login POST.
    // Then optionally validate OAuth client params.
    useEffect(() => {
        const run = async () => {
            try {
                const params = new URLSearchParams(window.location.search);
                const response_type = params.get("response_type");
                const client_id = params.get("client_id");
                const redirect_uri = params.get("redirect_uri");
                const state = params.get("state");
                const isOAuth2 = response_type === "code" && client_id && redirect_uri && state;

                const status = await prepareLoginSession({
                    fetchSession: () =>
                        fetchWithError<{ session?: unknown }>("/session", {
                            credentials: "include"
                        }),
                    clearStaleSession: () =>
                        fetch("/auth/logout", { credentials: "include" })
                });

                if (status === "authenticated" && !isOAuth2) {
                    navigate("/app/profile", { replace: true });
                    return;
                }

                if (!isOAuth2) {
                    return;
                }

                // Validate client exists before storing params or redirecting
                try {
                    const validateRes = await fetch(
                        `/auth/oauth/client-branding?client_id=${encodeURIComponent(client_id!)}`,
                        { credentials: "include" }
                    );
                    if (!validateRes.ok) {
                        const body = await validateRes.json().catch(() => ({}));
                        const message =
                            (body && typeof body.error_description === "string") ?
                                body.error_description
                                : "Die angeforderte Anwendung ist nicht registriert.";
                        setClientValidationError(message);
                        return;
                    }
                    const body = await validateRes.json().catch(() => ({}));
                    const clientName =
                        body && body.client && typeof body.client.name === "string"
                            ? body.client.name
                            : null;
                    const clientIconUrl =
                        body && body.client && typeof body.client.iconDataUrl === "string"
                            ? body.client.iconDataUrl
                            : null;
                    setOauthClientName(clientName);
                    setOauthClientIconUrl(clientIconUrl);
                    setClientValidationError(null);
                } catch {
                    setClientValidationError("Die Anwendung konnte nicht geprüft werden. Bitte versuchen Sie es später erneut.");
                    return;
                }
                // Store OAuth params in cookies only after successful validation
                document.cookie = `oauth_response_type=${response_type}; path=/; max-age=300; SameSite=Lax`;
                document.cookie = `oauth_client_id=${client_id}; path=/; max-age=300; SameSite=Lax`;
                document.cookie = `oauth_redirect_uri=${redirect_uri}; path=/; max-age=300; SameSite=Lax`;
                document.cookie = `oauth_state=${state}; path=/; max-age=300; SameSite=Lax`;
                if (status === "authenticated") {
                    const authorizeUrl = new URL("/auth/oauth/authorize", window.location.origin);
                    authorizeUrl.searchParams.append("response_type", response_type!);
                    authorizeUrl.searchParams.append("client_id", client_id!);
                    authorizeUrl.searchParams.append("redirect_uri", redirect_uri!);
                    authorizeUrl.searchParams.append("state", state!);
                    window.location.href = authorizeUrl.href;
                }
            } finally {
                setLoginReady(true);
            }
        };
        run();
    }, [navigate]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!loginReady) {
            return;
        }
        setIsLoading(true);

        try {
            // Submit as real form POST so backend can respond with server-side redirects.
            const form = document.createElement("form");
            form.method = "POST";
            form.action = "/auth/login";
            form.style.display = "none";

            const emailInput = document.createElement("input");
            emailInput.type = "hidden";
            emailInput.name = "email";
            emailInput.value = email;
            form.appendChild(emailInput);

            const passwordInput = document.createElement("input");
            passwordInput.type = "hidden";
            passwordInput.name = "password";
            passwordInput.value = password;
            form.appendChild(passwordInput);

            document.body.appendChild(form);
            form.submit();
        } catch (error) {
            // Display the error message to the user
            if (error instanceof Error) {
                enqueueSnackbar(error.message, { variant: 'error' });
            } else {
                enqueueSnackbar("Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie den Vorgang erneut.", { variant: 'error' });
            }
            setIsLoading(false);
        }
    };

    return (
        <AuthPageShell
            isDarkTheme={isDarkTheme}
            desktopSidePanel={
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        color: isDarkTheme ? "white" : "text.primary",
                        textAlign: "center",
                        px: 8,
                        py: 4,
                        transition: "all 0.4s ease-in-out"
                    }}
                >
                    <img
                        src={isDarkTheme ? tintoaLogoWhite : tintoaLogoBlack}
                        alt="tintoa-logo"
                        style={{
                            height: 64,
                            marginBottom: oauthClientName ? 32 : 0
                        }}
                    />
                    {oauthClientName && (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                px: 3,
                                py: 2,
                                border: 1,
                                borderColor: "divider",
                                borderRadius: 2,
                                bgcolor: isDarkTheme
                                    ? "rgba(22, 28, 36, 0.45)"
                                    : "rgba(255, 255, 255, 0.6)",
                                backdropFilter: "blur(8px)"
                            }}
                        >
                            {oauthClientIconUrl && (
                                <Box
                                    component="img"
                                    src={oauthClientIconUrl}
                                    alt={`${oauthClientName} icon`}
                                    onError={() => setOauthClientIconUrl(null)}
                                    sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: 1,
                                        objectFit: "cover",
                                        border: 1,
                                        borderColor: "divider"
                                    }}
                                />
                            )}
                            <Box sx={{ textAlign: "left" }}>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ fontWeight: 500 }}
                                >
                                    Du meldest dich an bei
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                    {oauthClientName}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Box>
            }
        >
            <Box
                sx={{
                    width: "100%",
                    maxWidth: { xs: 600, lg: "100%" },
                    mx: "auto",
                    display: "flex",
                    flexDirection: "column",
                    flex: { lg: 1 },
                    minHeight: { lg: 0 },
                    height: { lg: "100%" },
                    transition: "all 0.3s ease-in-out"
                }}
            >
                    <Paper
                        elevation={0}
                        sx={{
                            height: isLargeScreen ? '100%' : 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                            p: { xs: 3, sm: 4, lg: 5 },
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: isLargeScreen ? 0 : 2,
                            bgcolor: theme.palette.authCard!,
                            backdropFilter: 'blur(25px)',
                            boxShadow: 'none',
                            transition: 'all 0.3s ease-in-out'
                        }}
                    >
                        <Stack 
                            spacing={4} 
                            sx={{ 
                                flex: isLargeScreen ? 1 : 'none',
                                justifyContent: isLargeScreen ? 'center' : 'flex-start'
                            }}
                        >
                            {/* Logo and title for small screens */}
                            {!isLargeScreen && (
                                <Box sx={{ textAlign: 'center' }}>
                                    <img
                                        src={isDarkTheme ? tintoaLogoWhite : tintoaLogoBlack}
                                        alt="tintoa-logo"
                                        style={{ 
                                            height: 32,
                                            marginBottom: 24
                                        }}
                                    />
                                    <Typography 
                                        variant="h4" 
                                        component="h1" 
                                        gutterBottom
                                        sx={{ color: 'text.primary' }}
                                    >
                                        Willkommen zurück
                                    </Typography>
                                    <Typography 
                                        sx={{ 
                                            color: isDarkTheme ? 'text.secondary' : 'text.primary',
                                            opacity: isDarkTheme ? 1 : 0.7
                                        }}
                                    >
                                        Melde dich an, um fortzufahren
                                    </Typography>
                                </Box>
                            )}

                            {/* Title for large screens */}
                            {isLargeScreen && (
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography 
                                        variant="h4" 
                                        component="h1" 
                                        gutterBottom
                                        sx={{ color: 'text.primary' }}
                                    >
                                        Anmelden
                                    </Typography>
                                    <Typography 
                                        sx={{ 
                                            color: isDarkTheme ? 'text.secondary' : 'text.primary',
                                            opacity: isDarkTheme ? 1 : 0.7
                                        }}
                                    >
                                        Gib deine Anmeldedaten ein
                                    </Typography>
                                </Box>
                            )}

                            {clientValidationError && (
                                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setClientValidationError(null)}>
                                    {clientValidationError}
                                </Alert>
                            )}
                            {!isLargeScreen && oauthClientName && (
                                <Box
                                    sx={{
                                        border: 1,
                                        borderColor: "divider",
                                        borderRadius: 2,
                                        p: 2,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5,
                                        backgroundColor: isDarkTheme
                                            ? "rgba(22, 28, 36, 0.45)"
                                            : "rgba(255, 255, 255, 0.6)",
                                        backdropFilter: "blur(8px)"
                                    }}
                                >
                                    {oauthClientIconUrl && (
                                        <Box
                                            component="img"
                                            src={oauthClientIconUrl}
                                            alt={`${oauthClientName} icon`}
                                            onError={() => setOauthClientIconUrl(null)}
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                borderRadius: 1,
                                                objectFit: "cover",
                                                border: 1,
                                                borderColor: "divider"
                                            }}
                                        />
                                    )}
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            Du meldest dich an bei
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {oauthClientName}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                            <form onSubmit={handleSubmit}>
                                <Stack spacing={3}>
                                    <TextField
                                        label="E-Mail"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        fullWidth
                                        autoComplete="email"
                                        size={isLargeScreen ? "medium" : "small"}
                                    />
                                    <TextField
                                        label="Passwort"
                                        type={showPassword ? "text" : "password"}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        fullWidth
                                        autoComplete="current-password"
                                        size={isLargeScreen ? "medium" : "small"}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() => setShowPassword(!showPassword)}
                                                        edge="end"
                                                        size={isLargeScreen ? "medium" : "small"}
                                                    >
                                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        size={isLargeScreen ? "large" : "medium"}
                                        disabled={isLoading || !loginReady}
                                        fullWidth
                                        sx={{
                                            py: isLargeScreen ? 1.5 : 1,
                                            fontSize: isLargeScreen ? '1.1rem' : '1rem'
                                        }}
                                    >
                                        {isLoading ? "Wird angemeldet..." : "Anmelden"}
                                    </Button>
                                </Stack>
                            </form>

                            <Divider sx={{ my: 2 }}>
                                <Typography 
                                    variant="body2" 
                                    sx={{ 
                                        color: isDarkTheme ? 'text.secondary' : 'text.primary',
                                        opacity: 0.6,
                                        px: 2
                                    }}
                                >
                                    oder
                                </Typography>
                            </Divider>

                            <Button
                                variant="outlined"
                                size={isLargeScreen ? "large" : "medium"}
                                fullWidth
                                disabled={!loginReady}
                                onClick={() => {
                                    // Mark intent as login so backend does not create a new account
                                    document.cookie = 'oauth_intent=login; path=/; max-age=300; SameSite=Lax';
                                    // clientId is already stored in cookie from useEffect
                                    window.location.href = '/auth/google';
                                }}
                                sx={{
                                    py: isLargeScreen ? 1.5 : 1,
                                    fontSize: isLargeScreen ? '1.1rem' : '1rem',
                                    borderColor: theme.palette.input!.border,
                                    color: 'text.primary',
                                    '&:hover': {
                                        borderColor: theme.palette.input!.borderFocused,
                                        backgroundColor: theme.palette.input!.background
                                    }
                                }}
                            >
                                <Box
                                    component="span"
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        mr: 1
                                    }}
                                >
                                    <svg
                                        width="18"
                                        height="18"
                                        viewBox="0 0 18 18"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <g fill="none" fillRule="evenodd">
                                            <path
                                                d="M17.64 9.2045c0-.6371-.0573-1.2516-.1636-1.8409H9v3.4814h4.8436c-.2086 1.125-.8427 2.0782-1.7955 2.7164v2.2581h2.9087c1.7023-1.5668 2.6836-3.8741 2.6836-6.6146z"
                                                fill={theme.palette.semantic!.brand.google.blue}
                                            />
                                            <path
                                                d="M9 18c2.4295 0 4.4673-.7955 5.9564-2.1805l-2.9087-2.2581c-.8046.54-1.8364.8591-3.0477.8591-2.3441 0-4.3273-1.5836-5.0382-3.7105H.9573v2.3318C2.4382 15.9836 5.4818 18 9 18z"
                                                fill={theme.palette.semantic!.brand.google.green}
                                            />
                                            <path
                                                d="M3.9614 10.71c-.18-.54-.2823-1.1168-.2823-1.71s.1023-1.17.2823-1.71V4.9582H.9573A8.9965 8.9965 0 0 0 0 9c0 1.4523.3477 2.8268.9573 4.0418l3.0041-2.3318z"
                                                fill={theme.palette.semantic!.brand.google.yellow}
                                            />
                                            <path
                                                d="M9 3.5795c1.3214 0 2.5082.4545 3.4409 1.3455l2.5818-2.5818C12.4636.8918 10.4255 0 9 0 5.4818 0 2.4382 2.0164.9573 4.9582L3.9614 7.29C4.6723 5.1636 6.6555 3.5795 9 3.5795z"
                                                fill={theme.palette.semantic!.brand.google.red}
                                            />
                                        </g>
                                    </svg>
                                </Box>
                                Mit Google anmelden
                            </Button>

                            <TermsAndConditions method="login" />

                            <Stack spacing={2}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography 
                                        variant="body2" 
                                        sx={{ 
                                            color: isDarkTheme ? 'text.secondary' : 'text.primary',
                                            opacity: isDarkTheme ? 1 : 0.7
                                        }}
                                    >
                                        Noch kein Konto?{" "}
                                        <Link 
                                            component="button"
                                            onClick={() => navigate('/app/signup')}
                                            underline="hover"
                                            sx={{ 
                                                color: 'primary.main',
                                                fontWeight: 500,
                                                border: 'none',
                                                background: 'none',
                                                p: 0,
                                                cursor: 'pointer'
                                            }}
                                        >
                                            Jetzt registrieren
                                        </Link>
                                    </Typography>
                                </Box>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography 
                                        variant="body2" 
                                        sx={{ 
                                            color: isDarkTheme ? 'text.secondary' : 'text.primary',
                                            opacity: isDarkTheme ? 1 : 0.7
                                        }}
                                    >
                                        <Link 
                                            component="button"
                                            onClick={() => {
                                                    navigate('/app/send-password-reset');
                                            }}
                                            underline="hover"
                                            sx={{ 
                                                color: 'primary.main',
                                                fontWeight: 500,
                                                border: 'none',
                                                background: 'none',
                                                p: 0,
                                                cursor: 'pointer'
                                            }}
                                        >
                                            Passwort vergessen?
                                        </Link>
                                    </Typography>
                                </Box>
                            </Stack>
                        </Stack>
                    </Paper>
                </Box>
        </AuthPageShell>
    );
};
